import { interpolateRdYlBu, interpolateReds } from "d3-scale-chromatic";
import useChartDimensions from "../hooks/UseChartDimensions";
import { LegendProps } from "../interfaces/Legend";
import {
  getFontColor,
  getStrokeFromColor,
  lightColor,
} from "../services/VisServices";
import { loadFootCode } from "../services/Foot";

//Is needed for the dms component
const chartSettings = {};

/**
 * Legend that is shown in the first accordion besides/under the main
 * visualization. UI generation code was transferred into extra
 * functions.
 *
 * @param props Legend properties (what mode is currently active)
 */
function Legend(props: LegendProps) {
  const [ref, dms] = useChartDimensions(chartSettings, 40);

  /**
   * Calculates the legends height in pixels (of the SVG container) based on the currently
   * selected mode
   * @returns height in pixels
   */
  function getLegendHeight() {
    if (props.showTangentBodyParts) {
      return 375;
    } else if (props.showTransitions) {
      return 275;
    } else if (props.showShapes) {
      return 200;
    } else {
      return 90;
    }
  }

  /**
   * Calls the fitting UI code generation methods based on the currently selected mode.
   * All figures here have constant size and were tested with smartphones with a minimal
   * width of 360 pixels (in the Firefox Mobile Device Emulator)
   */
  function legendContent() {
    if (props.showHeatmap) {
      return heatmapLegend();
    } else if (props.showOrientations) {
      return orientationsLegend();
    } else if (props.showTransitions) {
      return transitionsLegend();
    } else if (props.showShapes) {
      return shapesLegend();
    } else if (props.showTangentBodyParts) {
      return tangentBodyPartsLegend();
    } else {
      return defaultLegend();
    }
  }

  function tangentBodyPartsLegend() {
    return (
      <g transform="translate(30,50)">
        {loadFootCode(
          "translate(0,0)",
          0.003,
          String(props.gentlemanColor),
          false,
          true
        )}
        <text x="40" y="-15">
          Gentleman Left Foot
        </text>

        {loadFootCode(
          "translate(0,50)",
          0.003,
          String(props.gentlemanColor),
          true,
          true
        )}
        <text x="40" y="35">
          Gentleman Right Foot
        </text>

        {loadFootCode(
          "translate(0,100)",
          0.003,
          String(props.ladyColor),
          false,
          true
        )}
        <text x="40" y="85">
          Lady Left Foot
        </text>

        {loadFootCode(
          "translate(0,150)",
          0.003,
          String(props.ladyColor),
          true,
          true
        )}
        <text x="40" y="135">
          Lady Right Foot
        </text>

        {loadFootCode(
          "translate(0,200)",
          0.003,
          String(props.gentlemanColor),
          false,
          true
        )}
        {loadFootCode(
          "translate(0,200)",
          0.003,
          String(props.gentlemanColor),
          true,
          true
        )}
        <text x="40" y="185">
          Gentleman Body Center
        </text>

        {loadFootCode(
          "translate(0,250)",
          0.003,
          String(props.ladyColor),
          false,
          true
        )}
        {loadFootCode(
          "translate(0,250)",
          0.003,
          String(props.ladyColor),
          true,
          true
        )}
        <text x="40" y="235">
          Lady Body Center
        </text>

        {loadFootCode(
          "translate(0,300)",
          0.003,
          String(props.coupleColor),
          false,
          true
        )}
        {loadFootCode(
          "translate(0,300)",
          0.003,
          String(props.coupleColor),
          true,
          true
        )}
        <text x="40" y="285">
          Couple Center
        </text>
      </g>
    );
  }

  /**
   * The legend displays a thick bar with the color gradient used for the heatmap.
   * The lowest/highest frequencies are displayed as numbers besides the gradient on the
   * left/right side.
   */
  function heatmapLegend() {
    return (
      <g transform="translate(20,50)">
        <text fontSize="15" x="0" y="-20">
          How often dancers are at this position
        </text>

        <text fontSize="15" x="-20" y="5">
          0
        </text>

        <text fontSize="15" x="260" y="5">
          {props.highestFrequency}
        </text>

        <linearGradient
          id={`transition-gradient-legend-heatmap`}
          gradientUnits="userSpaceOnUse"
          x1={0}
          y1={0}
          x2={250}
          y2={0}
        >
          <>
            <stop offset="0%" stopColor={interpolateReds(0)} />
            <stop offset="100%" stopColor={interpolateReds(1)} />
          </>
        </linearGradient>

        <line
          x1={0}
          y1={0}
          x2={250}
          y2={0}
          stroke={`url(#transition-gradient-legend-heatmap`}
          strokeWidth="15"
        />
      </g>
    );
  }

  /**
   * The legend displays a diamond shaped containing 4 gentlemen with ids 1 to 4.
   * Additional textual descriptions are attached at the right hand of the figure.
   */
  function shapesLegend() {
    return (
      <g transform="translate(0,50)">
        <line x1={85} y1={0} x2={155} y2={0} stroke="gray" strokeWidth="1" />
        <line x1={100} y1={75} x2={155} y2={75} stroke="gray" strokeWidth="1" />

        <text
          x={160}
          y={0}
          className="unselectable-text"
          dominantBaseline="middle"
          fill="black"
          fontSize="15"
        >
          Gentleman
        </text>

        <text
          x={160}
          y={75}
          className="unselectable-text"
          dominantBaseline="middle"
          fill="black"
          fontSize="15"
        >
          <tspan>Selected characteristic</tspan>
          <tspan dx={-150} dy={20}>
            shape
          </tspan>
        </text>

        <polygon points="70,0 20,50 70,100 120,50 " fill={"#c3e8fa"} />

        <circle
          cx="70"
          cy="0"
          r="12"
          fill={lightColor(String(props.gentlemanColor), 0.6)}
        />

        <circle
          cx="20"
          cy="50"
          r="12"
          fill={lightColor(String(props.gentlemanColor), 0.6)}
        />

        <circle
          cx="70"
          cy="100"
          r="12"
          fill={lightColor(String(props.gentlemanColor), 0.6)}
        />

        <circle
          cx="120"
          cy="50"
          r="12"
          fill={lightColor(String(props.gentlemanColor), 0.6)}
        />

        <text
          x="70"
          y="0"
          className="unselectable-text"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="15"
          fontWeight="bold"
          fill="white"
        >
          {props.colorCodedCouples ? "G" : "1"}
        </text>

        <text
          x="20"
          y="50"
          className="unselectable-text"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="15"
          fontWeight="bold"
          fill="white"
        >
          {props.colorCodedCouples ? "G" : "2"}
        </text>

        <text
          x="70"
          y="100"
          className="unselectable-text"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="15"
          fontWeight="bold"
          fill="white"
        >
          {props.colorCodedCouples ? "G" : "3"}
        </text>

        <text
          x="120"
          y="50"
          className="unselectable-text"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="15"
          fontWeight="bold"
          fill="white"
        >
          {props.colorCodedCouples ? "G" : "4"}
        </text>
      </g>
    );
  }

  /**
   * The legend displays a gentleman, who is rotated to the front and is also facing that direction.
   * Additional textual descriptions are attached at the right hand of the figure.
   */
  function orientationsLegend() {
    const radius = 20;

    return (
      <g transform="translate(50,50)">
        <line
          x1={10}
          y1={-1.7 * radius}
          x2={80}
          y2={-1.7 * radius}
          stroke="gray"
          strokeWidth="1"
        />

        <text
          x={85}
          y={-1.7 * radius}
          className="unselectable-text"
          dominantBaseline="middle"
          fill="black"
          fontSize="15"
        >
          Head orientation
        </text>

        <line
          x1={27}
          y1={-0.5 * radius}
          x2={80}
          y2={-0.5 * radius}
          stroke="gray"
          strokeWidth="1"
        />

        <text
          x={85}
          y={-0.5 * radius}
          className="unselectable-text"
          dominantBaseline="middle"
          fill="black"
          fontSize="15"
        >
          Body orientation
        </text>

        <line
          x1={0}
          y1={0}
          x2={0}
          y2={-1.7 * radius}
          stroke="black"
          strokeWidth="3"
        />
        <path
          fill={String(props.gentlemanColor)}
          stroke={getStrokeFromColor(String(props.gentlemanColor), 0.2)}
          d={
            "M " +
            -radius +
            " 0 A " +
            radius +
            " " +
            radius +
            " 0 0 1 " +
            radius +
            " 0"
          }
        />
        <path
          fill={lightColor(String(props.gentlemanColor), 0.6)}
          stroke={getStrokeFromColor(String(props.gentlemanColor), 0.2)}
          d={
            "M " +
            -radius +
            " 0 A " +
            radius +
            " " +
            radius +
            " 0 0 1 " +
            radius +
            " 0"
          }
          transform={`rotate(180)`}
        />
        <text
          fill="white"
          className="unselectable-text"
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            fontSize: 20,
            transform: "translate(0px, 7px)",
          }}
        >
          {props.colorCodedCouples ? "G" : "1"}
        </text>
      </g>
    );
  }

  /**
   * The legend displays a transition of a gentleman with 1 intermediate stop.
   * Additional textual descriptions are attached.
   * Below the color coding gets explained by using a simple color gradient
   */
  function transitionsLegend() {
    return (
      <g transform="translate(20,50)">
        <text x="0" y="-20" className="unselectable-text">
          Position in pattern before
        </text>

        <text x="135" y="40" className="unselectable-text">
          Intermediate position
        </text>

        <text x="100" y="140" className="unselectable-text">
          Position in current pattern
        </text>

        <linearGradient
          id={`transition-gradient-legend-0`}
          gradientUnits="userSpaceOnUse"
          x1={0}
          y1={0}
          x2={125}
          y2={0}
        >
          <>
            <stop offset="0%" stopColor={interpolateRdYlBu(0)} />
            <stop offset="100%" stopColor={interpolateRdYlBu(0.5)} />
          </>
        </linearGradient>

        <linearGradient
          id={`transition-gradient-legend-1`}
          gradientUnits="userSpaceOnUse"
          x1={125}
          y1={0}
          x2={250}
          y2={0}
        >
          <>
            <stop offset="0%" stopColor={interpolateRdYlBu(0.5)} />
            <stop offset="100%" stopColor={interpolateRdYlBu(1.0)} />
          </>
        </linearGradient>

        <line
          x1={0}
          y1={0}
          x2={125}
          y2={50}
          stroke={`url(#transition-gradient-legend-0`}
          strokeWidth="3"
        />
        <line
          x1={125}
          y1={50}
          x2={250}
          y2={100}
          stroke={`url(#transition-gradient-legend-1`}
          strokeWidth="3"
        />

        <circle cx="125" cy="50" fill="black" r={6} />

        <circle
          cx="0"
          cy="0"
          fill={lightColor(String(props.gentlemanColor), 0.6)}
          r={12}
        />
        <text
          fill="white"
          className="unselectable-text"
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            fontSize: 15,
            transform: "translate(0px, 2px)",
          }}
        >
          {props.colorCodedCouples ? "G" : "1"}
        </text>

        <circle cx="250" cy="100" fill={String(props.gentlemanColor)} r={20} />
        <text
          fill="white"
          className="unselectable-text"
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            fontSize: 20,
            transform: "translate(250px, 103px)",
          }}
        >
          {props.colorCodedCouples ? "G" : "1"}
        </text>

        <text
          className="unselectable-text"
          style={{
            transform: "translate(0px, 170px)",
          }}
        >
          Color represents passed time{" "}
        </text>

        <linearGradient
          id={`transition-gradient-legend-2`}
          gradientUnits="userSpaceOnUse"
          x1={0}
          y1={190}
          x2={125}
          y2={190}
        >
          <>
            <stop offset="0%" stopColor={interpolateRdYlBu(0)} />
            <stop offset="100%" stopColor={interpolateRdYlBu(0.5)} />
          </>
        </linearGradient>

        <linearGradient
          id={`transition-gradient-legend-3`}
          gradientUnits="userSpaceOnUse"
          x1={125}
          y1={190}
          x2={250}
          y2={190}
        >
          <>
            <stop offset="0%" stopColor={interpolateRdYlBu(0.5)} />
            <stop offset="100%" stopColor={interpolateRdYlBu(1)} />
          </>
        </linearGradient>

        <line
          x1={0}
          y1={190}
          x2={125}
          y2={190}
          stroke={`url(#transition-gradient-legend-2`}
          strokeWidth="15"
        />

        <line
          x1={125}
          y1={190}
          x2={250}
          y2={190}
          stroke={`url(#transition-gradient-legend-3`}
          strokeWidth="15"
        />
      </g>
    );
  }

  /**
   * This function displays three circles (one for a gentleman, a lady and a pair respectively).
   * The text "Gentleman/Lady/Pair 1" will be displayed above.
   *
   * The elements will have constant size (uncomment sections below to make it resize with the window).
   * However this is not necessary (Code works for all common screen widths).
   *
   * @returns UI generation code for the legend of the default view
   */
  function defaultLegend() {
    return (
      <g transform="translate(50,50)">
        <text x="0" y="-30" textAnchor="middle" className="unselectable-text">
          Gentleman
        </text>
        <circle
          cx="0"
          cy="0"
          fill={
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.gentlemanColor)
          }
          stroke={getStrokeFromColor(
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.gentlemanColor),
            0.2
          )}
          r={20}
        />
        <text
          fill={getFontColor(
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.gentlemanColor)
          )}
          className="unselectable-text"
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            fontSize: 20,
            transform: "translate(0px, 3px)",
          }}
        >
          {props.colorCodedCouples ? "G" : 1}
        </text>
        <text x="100" y="-30" textAnchor="middle" className="unselectable-text">
          Lady
        </text>
        <circle
          cx="100"
          cy="0"
          fill={
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.ladyColor)
          }
          stroke={getStrokeFromColor(
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.ladyColor),
            0.2
          )}
          r={20}
        />
        <text
          fill={getFontColor(
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.ladyColor)
          )}
          className="unselectable-text"
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            fontSize: 20,
            transform: "translate(100px, 3px)",
          }}
        >
          {props.colorCodedCouples ? "L" : 1}
        </text>
        <text x="200" y="-30" textAnchor="middle" className="unselectable-text">
          Pair
        </text>
        <circle
          cx="200"
          cy="0"
          fill={
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.coupleColor)
          }
          stroke={getStrokeFromColor(
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.coupleColor),
            0.2
          )}
          r={20}
        />
        <text
          fill={getFontColor(
            props.colorCodedCouples
              ? props.coupleColors[1].fillColor
              : String(props.coupleColor)
          )}
          className="unselectable-text"
          textAnchor="middle"
          alignmentBaseline="middle"
          style={{
            fontSize: 20,
            transform: "translate(200px, 3px)",
          }}
        >
          {props.colorCodedCouples ? "C" : 1}
        </text>
      </g>
    );
  }

  return (
    <div ref={ref} id="chartContainer">
      <svg width="320" height={getLegendHeight()}>
        {legendContent()}
      </svg>
    </div>
  );
}

export default Legend;
